'use client';

import React, { isValidElement, useContext, type FC, type ReactNode } from 'react';
import type { ListBoxItemProps as AriaListBoxItemProps } from 'react-aria-components';
import { ListBoxItem as AriaListBoxItem, Text as AriaText } from 'react-aria-components';
import { Check } from '@untitledui/icons';
import { cx } from '@milanote/roma/utils/cx';
import { SelectContext, type SelectItemType } from './select';
import { isReactComponent } from '../../../utils/is-react-component';
import type { AvatarProps } from '../avatar/avatar';
import { AvatarLabelGroup } from '../avatar/avatar-label-group';

const SEMANTIC_NAME_BASE = 'RomaSelectItemBase';
const SEMANTIC_NAME = 'RomaSelectItem';

const sizes = {
    sm: {
        root: 'p-2 pl-2 text-sm',
    },
    md: {
        root: 'p-2.5 pl-2.5 text-md',
    },
};

export type SelectItemBaseProps = {
    label?: string;
    avatar?: AvatarProps;
    supportingText?: string;
    icon?: FC | ReactNode;
    size?: 'sm' | 'md';
    isSelected?: boolean;
    isDisabled?: boolean;
    isFocused?: boolean;
    isFocusVisible?: boolean;
    children?: ReactNode;
    className?: string;
};

export const SelectItemBase = ({
    label,
    avatar,
    supportingText,
    icon: Icon,
    size: sizeProp,
    isSelected,
    isDisabled,
    isFocused,
    isFocusVisible,
    children,
    className,
}: SelectItemBaseProps) => {
    const { size: contextSize } = useContext(SelectContext);
    const size = sizeProp ?? contextSize;

    return (
        <div
            data-in-select-item
            className={cx(
                SEMANTIC_NAME_BASE,
                'flex cursor-pointer items-center gap-2 rounded-xs outline-hidden select-none text-primary',
                isSelected && 'bg-active',
                isDisabled && 'cursor-not-allowed',
                isFocused && 'bg-primary_hover',
                isFocusVisible && 'ring-2 ring-focus-ring ring-inset',

                // Icon styles
                '*:data-icon:size-5 *:data-icon:shrink-0 *:data-icon:text-fg-quaternary',
                isDisabled && '*:data-icon:text-fg-disabled',

                // Size styles
                '*:data-in-select-item',

                sizes[size].root,
                className,
            )}
        >
            {avatar && (label || supportingText) ? (
                <AvatarLabelGroup
                    {...avatar}
                    size={size}
                    title={label || children}
                    subtitle={supportingText || ''}
                    className="-my-1"
                />
            ) : isReactComponent(Icon) ? (
                <Icon data-icon aria-hidden="true" />
            ) : isValidElement(Icon) ? (
                Icon
            ) : null}

            {!avatar && (
                <div className="flex w-full min-w-0 flex-1 flex-wrap gap-x-xs">
                    <AriaText
                        slot="label"
                        className={cx('truncate font-medium whitespace-nowrap', isDisabled && 'text-disabled')}
                    >
                        {label || children}
                    </AriaText>

                    {supportingText && (
                        <AriaText
                            slot="description"
                            className={cx('whitespace-nowrap truncate text-tertiary', isDisabled && 'text-disabled')}
                        >
                            {supportingText}
                        </AriaText>
                    )}
                </div>
            )}

            {isSelected && (
                <Check
                    aria-hidden="true"
                    className={cx(
                        'ml-auto text-fg-primary',
                        size === 'sm' ? 'size-4 stroke-[2.5px]' : 'size-5',
                        isDisabled && 'text-fg-disabled',
                    )}
                />
            )}
        </div>
    );
};

interface SelectItemProps extends Omit<AriaListBoxItemProps<SelectItemType>, 'id'>, SelectItemType {}

export const SelectItem = ({
    label,
    id,
    value,
    avatar,
    supportingText,
    isDisabled,
    icon,
    className,
    children,
    ...props
}: SelectItemProps) => {
    const labelOrChildren = label || (typeof children === 'string' ? children : '');
    const textValue = supportingText ? labelOrChildren + ' ' + supportingText : labelOrChildren;

    return (
        <AriaListBoxItem
            id={id}
            value={
                value ?? {
                    id,
                    label: labelOrChildren,
                    avatar,
                    supportingText,
                    isDisabled,
                    icon,
                }
            }
            textValue={textValue}
            isDisabled={isDisabled}
            {...props}
            className={(state) =>
                cx('w-full px-1.5 py-px outline-hidden', typeof className === 'function' ? className(state) : className)
            }
        >
            {(state) => (
                <SelectItemBase
                    label={label}
                    avatar={avatar}
                    supportingText={supportingText}
                    icon={icon}
                    isSelected={state.isSelected}
                    isDisabled={state.isDisabled}
                    isFocused={state.isFocused}
                    isFocusVisible={state.isFocusVisible}
                >
                    {typeof children === 'function' ? children(state) : children}
                </SelectItemBase>
            )}
        </AriaListBoxItem>
    );
};

SelectItemBase.displayName = SEMANTIC_NAME_BASE;
SelectItem.displayName = SEMANTIC_NAME;
